import type { ReactNode } from 'react';
import { Bot } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export type AgentOrbState = 'working' | 'needs-you' | 'asked' | 'failed' | 'finished' | 'idle' | 'ended';

export interface AgentOrbProps {
  state: AgentOrbState;
  /** Diameter in px: 32 on a card, 24 in a row. */
  size?: number;
  /** The agent's logo or glyph in the middle; a bot by default. */
  icon?: ReactNode;
  /**
   * The agent's own colour as a text class (text-data-2 for Claude): the ripple takes it, so a
   * working agent breathes in its own brand colour.
   */
  hue?: string;
  /** Accessible state: "Working", "Needs you". */
  label: string;
  className?: string;
}

/** The edge of the mark per state: a ring that reads at a glance, or none. */
const EDGE: Partial<Record<AgentOrbState, string>> = {
  'needs-you': 'ring-2 ring-status-attention-fill outline-solid outline-4 outline-status-attention-soft',
  asked: 'outline-dashed outline-1 outline-offset-1 outline-fg-subtle',
  failed: 'ring-2 ring-status-danger-fill',
};

/**
 * A small dot at the corner, for an outcome. None today: finished work is quiet grey like idle and
 * ended (a list of finished agents must not be speckled green); green is kept for a deliberate
 * success moment, never a resting state.
 */
const DOT: Partial<Record<AgentOrbState, string>> = {};

/**
 * An agent at a glance: its own logo in a small round mark. Working: circular waves ripple out in
 * the agent's own colour (the one perpetual motion, a running indicator). Needs you: a steady
 * amber ring with a soft halo. Asked: a dashed grey ring. Failed: a red ring. Finished, idle and
 * ended: the logo in grayscale, quiet (ended dimmer). Under reduced motion the waves are gone.
 */
export function AgentOrb({ state, size = 32, icon, hue, label, className }: AgentOrbProps) {
  const resting = state === 'idle' || state === 'ended' || state === 'finished';
  return (
    <span role="img" aria-label={label} className={cn('relative inline-flex shrink-0 items-center justify-center', hue, className)} style={{ width: size, height: size }}>
      {state === 'working' && (
        <>
          <span aria-hidden className="motion-ripple absolute inset-0 rounded-pill border border-current" />
          <span aria-hidden className="motion-ripple absolute inset-0 rounded-pill border border-current" style={{ animationDelay: 'calc(var(--duration-spin) * 2)' }} />
        </>
      )}
      <span
        aria-hidden
        className={cn(
          'relative flex size-full items-center justify-center rounded-pill bg-raised text-fg-muted',
          size <= 24 ? '[&_svg]:size-3' : '[&_svg]:size-4',
          resting && 'grayscale',
          state === 'ended' && 'opacity-50',
          EDGE[state],
          state === 'idle' && 'opacity-70',
        )}
      >
        {icon ?? <Bot />}
      </span>
      {DOT[state] && <span aria-hidden className={cn('absolute -right-0.5 -top-0.5 size-2 rounded-pill ring-2 ring-surface', DOT[state])} />}
    </span>
  );
}
